import * as MantineCore from '@mantine/core';
import {
	createTheme,
	DEFAULT_THEME,
	type MantineColor,
	type MantineColorsTuple,
	type MantineThemeComponents,
	type MantineThemeOverride,
} from '@mantine/core';

const fontFallback = `-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`;

export interface ThemeBuilderProps {
	fontFamily?: {
		heading: string;
		body: string;
	};
	reducedMotion?: boolean | undefined;
	colors: {
		preset?: {
			primary: MantineColor;
			secondary: MantineColor;
		};
		custom?: {
			primary: MantineColorsTuple;
			secondary: MantineColorsTuple;
		};
		dark?: MantineColorsTuple;
	};
}

const sizeMd = { size: 'md' };
const radiusMd = { radius: 'md' };

// Avoid overriding same defaultProps, because it will force them to use it and inheritance won't work ok
// e.g. <Text size='sm'><Anchor>Something</Anchor></Text> will Anchor will be size='md' instead of 'sm'
const defaultProps = {
	// Applied to all components.
	//'*': {},

	// Inputs
	Checkbox: {
		...sizeMd,
		...radiusMd,
	},
	CheckboxIndicator: radiusMd,
	Chip: sizeMd,
	ColorInput: sizeMd,
	ColorPicker: sizeMd,
	FileInput: sizeMd,
	Input: sizeMd,
	JsonInput: sizeMd,
	NativeSelect: sizeMd,
	NumberInput: sizeMd,
	PasswordInput: sizeMd,
	PinInput: sizeMd,
	Radio: {
		...sizeMd,
		...radiusMd,
	},
	RadioIndicator: radiusMd,
	Rating: sizeMd,
	SegmentedControl: sizeMd,
	// Slider is already sizeMd
	Switch: sizeMd,
	Textarea: sizeMd,
	TextInput: sizeMd,

	InputBase: sizeMd,

	// Combobox
	Autocomplete: sizeMd,
	Combobox: sizeMd,
	MultiSelect: sizeMd,
	Pill: sizeMd,
	PullInput: sizeMd,
	Select: sizeMd,
	TagsInput: sizeMd,

	// Buttons
	Button: sizeMd,
};

export const buildTheme = ({ fontFamily, reducedMotion, colors }: ThemeBuilderProps): MantineThemeOverride => {
	if (!colors.preset && !colors.custom) {
		throw new Error('You must provide a color preset or custom colors');
	}

	const config: MantineThemeOverride = {
		fontFamily: `${fontFamily?.body || '"Mulish"'}, ${fontFallback}`,
		headings: {
			fontFamily: `${fontFamily?.heading || '"Mulish"'}, ${fontFallback}`,
		},

		black: 'rgba(0, 0, 0, 0.84)',

		primaryColor: 'primary',

		autoContrast: true,
		//luminanceThreshold: 0.33,

		spacing: {
			'4xs': 'calc(0.125rem * var(--mantine-scale))',
			'3xs': 'calc(0.265rem * var(--mantine-scale))',
			'2xs': 'calc(0.425rem * var(--mantine-scale))',
			'2xl': 'calc(3rem * var(--mantine-scale))',
			'3xl': 'calc(4rem * var(--mantine-scale))',
		},

		defaultRadius: 'lg',

		respectReducedMotion: reducedMotion,

		components: findExtendableComponents(MantineCore).reduce(
			(acc, { name, component }) => {
				//const props = { ...defaultProps['*'] }; // Enable this is we add * to defaultProps
				const props = {};
				if (name in defaultProps) {
					Object.assign(props, defaultProps[name as keyof typeof defaultProps]);
				}
				acc[name] = component.extend({ defaultProps: props });
				return acc;
			},
			{} as Partial<MantineThemeComponents>,
		),
	};

	if (colors.preset) {
		// Uses mantine colors
		config.primaryColor = colors.preset.primary;
		config.colors = {
			primary: DEFAULT_THEME.colors[colors.preset.primary],
			secondary: DEFAULT_THEME.colors[colors.preset.secondary],
		};
	} else {
		// Sets custom colors
		config.colors = {
			primary: colors.custom?.primary,
			secondary: colors.custom?.secondary,
		};
	}

	// New dark mode
	config.colors = {
		...config.colors,
		dark: colors.dark
			? colors.dark
			: ['#C9C9C9', '#b8b8b8', '#828282', '#424242', '#3b3b3b', '#2e2e2e', '#242424', '#1f1f1f', '#121212', '#000000'],
	};

	return createTheme(config);
};

type ExtendableComponent = { extend: (input: Record<string, unknown>) => ExtendableComponent; defaultProps?: Record<string, unknown> };
function findExtendableComponents(root: Record<string, unknown>): {
	name: string;
	component: ExtendableComponent;
}[] {
	return Object.entries(root)
		.filter(([, component]) => typeof component === 'object' && component !== null && Object.hasOwn(component, 'extend'))
		.flatMap(([name, component]) => [
			{
				name,
				component: component as ExtendableComponent,
			},
			...findExtendableComponents(component as Record<string, unknown>),
		]);
}
